import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import { dateRangeError } from '@/features/reports/settings';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import {
  runReports,
  runLabel,
  useMigration,
  useParallelRuns,
  type ParallelRunView,
  type RunReport,
} from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { i18n, useLanguage, useT } from '@/lib/i18n';

export const Route = createFileRoute('/reports/parallel-run/')({ component: ParallelRuns });

const monthAfter = (iso: string | null): { from: string; to: string } => {
  const base = iso ? new Date(`${iso}T00:00:00Z`) : new Date();
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth();
  const from = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
  return { from, to };
};

const reportTitle = (key: RunReport): string =>
  i18n.t(`reportPages.dashboard.parallelRun.report.${key}`);

/** A whole month's name in Arabic ("أكتوبر 2026", Western digits); a range stays as dates. */
const runLabelFor = (lang: 'en' | 'ar', label: string, from: string): string => {
  if (lang !== 'ar' || label.includes('/')) return label;
  const [y, m] = from.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('ar-AE-u-nu-latn', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
};

/** Screen R3 (document 06): months compared with QuickBooks during the parallel run. */
function ParallelRuns() {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const me = useMe();
  const runs = useParallelRuns();
  const migration = useMigration();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const first = monthAfter(migration.data?.conversionDate ?? null);
  const [from, setFrom] = useState(first.from);
  const [to, setTo] = useState(first.to);
  const rangeError = from
    ? dateRangeError(from, to)
    : t('reportPages.dashboard.parallelRun.chooseStart');
  const create = useMutation({
    mutationFn: () =>
      api<ParallelRunView>(`/orgs/${orgId}/parallel-runs`, {
        method: 'POST',
        body: JSON.stringify({ from, to }),
      }),
    onSuccess: async (v) => {
      await qc.invalidateQueries({ queryKey: ['parallel-runs', orgId] });
      void navigate({ to: '/reports/parallel-run/$runId', params: { runId: v.id } });
    },
    onError: (e) => {
      const id = (e as ApiError).details as { id?: string } | undefined;
      if (e instanceof ApiError && e.code === 'DUPLICATE' && id?.id)
        void navigate({ to: '/reports/parallel-run/$runId', params: { runId: id.id } });
    },
  });
  const staff = Boolean(me.data?.isStaff);
  return (
    <>
      <PageHeader
        title={t('reportPages.dashboard.parallelRun.listTitle')}
        description={t('reportPages.dashboard.parallelRun.listDescription')}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/reports">{t('reportPages.dashboard.parallelRun.allReports')}</Link>
            </Button>
            {staff ? (
              <Button
                onClick={() => {
                  const latest = runs.data?.[0]?.to;
                  // the month after the latest one compared, else the conversion month
                  const next = monthAfter(
                    latest
                      ? new Date(Date.parse(`${latest}T00:00:00Z`) + 86_400_000)
                          .toISOString()
                          .slice(0, 10)
                      : (migration.data?.conversionDate ?? null),
                  );
                  setFrom(next.from);
                  setTo(next.to);
                  setAdding(true);
                }}
              >
                {t('reportPages.dashboard.parallelRun.compareMonth')}
              </Button>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <div className="flex max-w-5xl flex-col gap-4">
          {adding ? (
            <div className="flex flex-col gap-2 rounded-lg border bg-card p-4">
              <ErrorBanner>
                {create.error && (create.error as ApiError).code !== 'DUPLICATE'
                  ? create.error.message
                  : null}
              </ErrorBanner>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-sm font-medium">
                  {t('reportPages.dashboard.parallelRun.from')}
                  <DateInput
                    value={from}
                    onChange={setFrom}
                    aria-label={t('reportPages.dashboard.parallelRun.from')}
                    className="w-40"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  {t('reportPages.dashboard.parallelRun.to')}
                  <DateInput
                    value={to}
                    onChange={setTo}
                    aria-label={t('reportPages.dashboard.parallelRun.to')}
                    className="w-40"
                  />
                </label>
                <Button
                  disabled={create.isPending || Boolean(rangeError)}
                  onClick={() => create.mutate()}
                >
                  {create.isPending
                    ? t('reportPages.dashboard.parallelRun.starting')
                    : t('reportPages.dashboard.parallelRun.startComparing')}
                </Button>
                <Button variant="outline" onClick={() => setAdding(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
              {rangeError ? (
                <p role="alert" className="text-sm text-destructive">
                  {rangeError}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {t('reportPages.dashboard.parallelRun.exportHint', {
                  reports: runReports()
                    .map((r) => reportTitle(r.key))
                    .join(lang === 'ar' ? '، ' : ', '),
                })}
              </p>
            </div>
          ) : null}
          {runs.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : runs.isError ? (
            <EmptyState
              title={t('reportPages.dashboard.parallelRun.loadListError')}
              hint={runs.error.message}
            />
          ) : runs.data.length === 0 ? (
            <EmptyState
              title={t('reportPages.dashboard.parallelRun.noneTitle')}
              hint={t('reportPages.dashboard.parallelRun.noneHint')}
            />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('reportPages.dashboard.parallelRun.colPeriod')}</TableHead>
                    <TableHead className="w-[180px]">
                      {t('reportPages.dashboard.parallelRun.colQboReports')}
                    </TableHead>
                    <TableHead className="w-[260px]">
                      {t('reportPages.dashboard.parallelRun.colStatus')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.data.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Link
                          to="/reports/parallel-run/$runId"
                          params={{ runId: r.id }}
                          className="font-medium text-primary hover:underline"
                        >
                          {runLabelFor(lang, runLabel(r.from, r.to), r.from)}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(r.from)} – {formatDate(r.to)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {t('reportPages.dashboard.parallelRun.uploadedOf', {
                          uploaded: r.uploaded,
                          total: runReports().length,
                        })}
                      </TableCell>
                      <TableCell>
                        {r.signedOffAt ? (
                          <StatusChip tone="success">
                            {r.signedOffBy
                              ? t('reportPages.dashboard.parallelRun.signedOffOnBy', {
                                  date: formatDate(r.signedOffAt.slice(0, 10)),
                                  name: r.signedOffBy,
                                })
                              : t('reportPages.dashboard.parallelRun.signedOffOn', {
                                  date: formatDate(r.signedOffAt.slice(0, 10)),
                                })}
                          </StatusChip>
                        ) : (
                          <StatusChip tone="info">
                            {t('reportPages.dashboard.parallelRun.open')}
                          </StatusChip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
